import { useState, useCallback } from 'react';
import { ReportData } from '../domain/types';

export type ReportStatus = 'idle' | 'charts' | 'rendering' | 'done' | 'error';

interface UseReportGeneratorReturn {
  generate: (data: ReportData) => Promise<void>;
  status: ReportStatus;
  error: string | null;
}

async function loadLogoAsDataUrl(): Promise<string | undefined> {
  try {
    const response = await fetch('/quantum-small.png');
    if (!response.ok) return undefined;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => resolve(undefined);
      reader.readAsDataURL(blob);
    });
  } catch {
    return undefined;
  }
}

export function useReportGenerator(): UseReportGeneratorReturn {
  const [status, setStatus] = useState<ReportStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  const generate = useCallback(async (data: ReportData) => {
    try {
      setStatus('charts');
      setError(null);

      // 1. Render impact bar charts + generation chart + lifetime chart
      const { renderAllCharts } = await import('../domain/chartRenderers');
      const chartImages = await renderAllCharts(
        data.impactBreakdown.health,
        data.impactBreakdown.biodiversity,
        data.impactBreakdown.economic,
        data.generation,
        data.lifetimeProjection,
        data.profileComparison,
        data.profileLabel,
      );

      // 2. Render map image (if we have project coordinates)
      if (data.projectConfig?.latitude != null && data.projectConfig?.longitude != null) {
        try {
          const { renderMapImage } = await import('../domain/mapRenderer');
          chartImages.map = await renderMapImage(
            data.projectConfig.latitude,
            data.projectConfig.longitude,
            data.projectConfig.eiaBaCode,
          );
        } catch (mapErr) {
          console.warn('Map rendering failed, skipping:', mapErr);
        }
      }

      // 3. Load logo
      const logoDataUrl = await loadLogoAsDataUrl();

      setStatus('rendering');

      // 4. Dynamic import @react-pdf/renderer
      const { pdf } = await import('@react-pdf/renderer');
      const { ReportDocument } = await import('../components/ReportDocument');

      // 5. Build PDF
      const doc = ReportDocument({ data, chartImages, logoDataUrl });
      const blob = await pdf(doc).toBlob();

      // 6. Trigger download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const safeName = data.project.projectName.replace(/[^a-zA-Z0-9_-]/g, '_');
      link.download = `${safeName}_TotalView_Report.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      setStatus('done');

      // Reset after a short delay
      setTimeout(() => setStatus('idle'), 2000);
    } catch (err) {
      console.error('Report generation failed:', err);
      setError(err instanceof Error ? err.message : String(err));
      setStatus('error');
    }
  }, []);

  return { generate, status, error };
}
